import { toaster } from "@/components/ui/toaster";
import { now } from "@/helpers/time";
import { useMerchant } from "@/hooks/useMerchant";
import useNumberFormatter from "@/hooks/useNumberFormatter";
import { Box, Button, Card, HStack, Text } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { formatCalendarDate } from "../contracts/columns";
import { Contract } from "../contracts/types";
import { useProtectTRPC } from "../trpc";
import AddContractSearch from "./components/AddContractSearch";
import ToClaimTable from "./components/ToClaimTable";
import {
  claimReportDeadline,
  isClaimReportWindowOpen,
  monthlyClaimAmount,
  reportablePeriods,
} from "./reporting";
import { ToClaimItem } from "./types";
import { useInvalidateClaims } from "./useInvalidateClaims";

type Props = {
  items: ToClaimItem[];
  setItems: React.Dispatch<React.SetStateAction<ToClaimItem[]>>;
  onClaimed: () => void;
};

const ToClaimTab = ({ items, setItems, onClaimed }: Props): JSX.Element => {
  const trpc = useProtectTRPC();
  const { merchant } = useMerchant();
  const invalidateClaims = useInvalidateClaims();
  const formatCurrency = useNumberFormatter("es-CO", "COP", "currency");

  const today = now();
  const isWindowOpen = isClaimReportWindowOpen(today);
  const deadline = formatCalendarDate(
    claimReportDeadline(today).format("YYYY-MM-DD")
  );
  const nextWindow = formatCalendarDate(
    today.add(1, "month").startOf("month").format("YYYY-MM-DD")
  );

  const selected = items.filter(
    (item) => item.selected && item.periods.length > 0
  );
  const selectedAmount = selected.reduce(
    (total, item) =>
      total + monthlyClaimAmount(item.contract) * item.periods.length,
    0
  );

  const { mutateAsync: reportClaim, isPending } = useMutation(
    trpc.claims.report.mutationOptions()
  );

  const updateItem = (
    contractId: string,
    update: (item: ToClaimItem) => ToClaimItem
  ) =>
    setItems((prev) =>
      prev.map((item) =>
        item.contract.id === contractId ? update(item) : item
      )
    );

  const handleAdd = (contract: Contract) => {
    const periods = reportablePeriods(today, contract.enrolledAt);

    setItems((prev) => [
      ...prev,
      { contract, periods, selected: periods.length > 0 },
    ]);
  };

  const handleTogglePeriod = (contractId: string, period: string) =>
    updateItem(contractId, (item) => {
      const periods = item.periods.includes(period)
        ? item.periods.filter((candidate) => candidate !== period)
        : [...item.periods, period];

      return {
        ...item,
        periods,
        selected: periods.length > 0 && item.selected,
      };
    });

  const handleRemove = (contractId: string) =>
    setItems((prev) => prev.filter((item) => item.contract.id !== contractId));

  const handleClaim = async () => {
    if (!merchant || selected.length === 0) return;

    const results = await Promise.allSettled(
      selected.map((item) =>
        reportClaim({
          rentalsMerchantId: merchant.id,
          contractId: item.contract.id,
          periods: item.periods,
        })
      )
    );
    const claimedIds = selected
      .filter((_, index) => results[index].status === "fulfilled")
      .map((item) => item.contract.id);
    const failed = selected.length - claimedIds.length;

    setItems((prev) =>
      prev.filter((item) => !claimedIds.includes(item.contract.id))
    );
    await invalidateClaims();

    if (claimedIds.length > 0)
      toaster.create({
        title: "Reclamación reportada",
        description: `${claimedIds.length} contrato(s) reclamado(s).`,
        type: "success",
        duration: 6000,
        meta: { closable: true },
      });
    if (failed > 0)
      toaster.create({
        title: "Error",
        description: `No pudimos reclamar ${failed} contrato(s). Siguen en la lista.`,
        type: "error",
        duration: 6000,
        meta: { closable: true },
      });
    if (failed === 0) onClaimed();
  };

  return (
    <Box display="flex" flexDirection="column" gap="1rem" flex="1" minH={0}>
      <Box bg="palomma.50" borderRadius="lg" p={4} flexShrink={0}>
        <Text fontSize="sm" color="gray.700">
          {isWindowOpen
            ? `Puedes reclamar cánones en mora hasta el ${deadline}. Cada reclamación es tu decisión.`
            : `El plazo para reclamar este mes terminó el ${deadline}. Podrás reclamar desde el ${nextWindow}.`}
        </Text>
      </Box>
      <AddContractSearch
        addedContractIds={items.map((item) => item.contract.id)}
        onAdd={handleAdd}
      />
      {selected.length > 0 && (
        <Card.Root
          borderRadius="lg"
          border="none"
          bg="palomma.50"
          flexShrink={0}
        >
          <Card.Body p="3">
            <HStack justify="space-between" gap={4}>
              <Text fontSize="sm" color="palomma.700">
                ✓ {selected.length} seleccionado(s) ·{" "}
                {formatCurrency(selectedAmount)}
              </Text>
              <Button
                size="sm"
                disabled={!isWindowOpen}
                loading={isPending}
                onClick={handleClaim}
              >
                Reclamar {selected.length} →
              </Button>
            </HStack>
          </Card.Body>
        </Card.Root>
      )}
      <ToClaimTable
        items={items}
        reportablePeriods={(item) =>
          reportablePeriods(today, item.contract.enrolledAt)
        }
        onToggleAll={(isSelected) =>
          setItems((prev) =>
            prev.map((item) => ({
              ...item,
              selected: isSelected && item.periods.length > 0,
            }))
          )
        }
        onToggle={(contractId, isSelected) =>
          updateItem(contractId, (item) => ({ ...item, selected: isSelected }))
        }
        onTogglePeriod={handleTogglePeriod}
        onRemove={handleRemove}
      />
      <Text fontSize="sm" color="gray.500">
        {items.length} en la lista
      </Text>
    </Box>
  );
};

export default ToClaimTab;
